import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Clapperboard, LogOut, Plus, Users, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, formatErr } from "@/lib/api";

const ChangePassword = ({ onClose }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      onClose();
    } catch (ex) { toast.error(formatErr(ex)); }
    finally { setBusy(false); }
  };
  const input = "w-full h-10 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none mb-3";
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm px-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="change-password-form" className="w-full max-w-sm rounded-xl border border-[#242C3D] bg-[#141A24] p-6 sv-rise">
        <h3 className="font-display font-semibold text-lg mb-5">Change password</h3>
        <input data-testid="change-password-current-input" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className={input} />
        <input data-testid="change-password-new-input" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password (8+ characters)" className={input} />
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-full border border-[#242C3D] text-sm hover:bg-white/5">Cancel</button>
          <button data-testid="change-password-submit-button" disabled={busy} className="h-9 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</button>
        </div>
      </form>
    </div>
  );
};

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [showPw, setShowPw] = useState(false);
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 sv-grid-bg">
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[#0B0F17]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/admin" data-testid="nav-brand-link" className="flex items-center gap-2.5 group">
            <span className="w-8 h-8 rounded-md bg-indigo-500 grid place-items-center transition-transform duration-300 group-hover:rotate-6">
              <Clapperboard className="w-4 h-4 text-white" />
            </span>
            <span className="font-display font-bold tracking-tight text-lg">StreamVault</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 ml-1 hidden sm:inline">HQ</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-8 mr-auto" data-testid="main-nav">
            {[["/admin", "Build", true], ["/admin/content", "Content"], ["/admin/reports", "Reports"], ...(user?.role === "admin" ? [["/admin/settings", "Settings"]] : [])].map(([to, label, end]) => (
              <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase()}-link`}
                className={({ isActive }) => `h-9 px-4 rounded-full text-sm grid place-items-center transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <Link data-testid="nav-team-link" to="/admin/team" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm transition-colors">
                <Users className="w-4 h-4" /> Team
              </Link>
            )}
            <button
              data-testid="nav-new-campaign-button"
              onClick={() => nav("/admin/programs/new")}
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> New campaign
            </button>
            <span data-testid="nav-user-email" className="hidden md:block font-mono text-xs text-slate-400">{user?.email}</span>
            <button data-testid="nav-change-password-button" onClick={() => setShowPw(true)} className="h-9 w-9 grid place-items-center rounded-full border border-white/10 hover:bg-white/5 transition-colors" title="Change password">
              <KeyRound className="w-4 h-4 text-slate-300" />
            </button>
            <button
              data-testid="nav-logout-button"
              onClick={async () => { await logout(); nav("/login"); }}
              className="h-9 w-9 grid place-items-center rounded-full border border-white/10 hover:bg-white/5 transition-colors"
              title="Log out"
            >
              <LogOut className="w-4 h-4 text-slate-300" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>
      {showPw && <ChangePassword onClose={() => setShowPw(false)} />}
    </div>
  );
}
